import * as React from "react";
import { CalendarPlus, MoreHorizontal, PencilIcon, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/ui/loader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useRetryBusinessReportOutcomeResultMutation } from "@/modules/snapshots/businessReports.api";
import { toast } from "react-toastify";
import { EventsDropdown } from "./EventsDropdown";
import type { GetSnapshotEventResponseItem } from "@/modules/snapshots/businessReports.types";
import { useNavigate } from "react-router";
import { useDateFormatter } from "@/hooks/useDateFormatter";

interface ActionMenuItemsProps {
  isRetrying: boolean;
  /** Omitted where nothing can regenerate the cell, e.g. a family summary row. */
  onRetry?: () => void;
  onEdit: () => void;
}

export const ActionMenuItems: React.FC<ActionMenuItemsProps> = ({ isRetrying, onRetry, onEdit }) => (
  <>
    {onRetry && (
      <DropdownMenuItem onClick={onRetry} disabled={isRetrying}>
        <RotateCw className="mr-2 h-3.5 w-3.5" />
        {isRetrying ? "Retrying..." : "Retry"}
      </DropdownMenuItem>
    )}
    <DropdownMenuItem onClick={onEdit}>
      <PencilIcon className="mr-2 h-3.5 w-3.5" />
      Edit
    </DropdownMenuItem>
  </>
);

interface SectionActionsProps {
  processing: boolean;
  isProcessing: boolean;
  businessReportOutcomeId: string;
  businessReportOutcomeResultId: string;
  onEdit: () => void;
  onSetProcessing: (isProcessing: boolean) => void;
  canRetry?: boolean;
}

export const SectionActions: React.FC<SectionActionsProps> = ({
  processing,
  isProcessing,
  businessReportOutcomeId,
  businessReportOutcomeResultId,
  onEdit,
  onSetProcessing,
  canRetry = true,
}) => {
  const [retryBusinessReportOutcomeResult, { isLoading: isRetrying }] = useRetryBusinessReportOutcomeResultMutation();

  const handleRetry = async () => {
    try {
      await retryBusinessReportOutcomeResult({
        resultId: businessReportOutcomeResultId,
        id: businessReportOutcomeId,
      }).unwrap();
      toast.success("Regenerating section...");
    } catch (error) {
      toast.error("Error while retrying");
      console.error(error);
    }
  };

  React.useEffect(() => {
    onSetProcessing(isRetrying);
  }, [isRetrying, onSetProcessing]);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Section actions"
          className="flex h-7 w-7 items-center justify-center rounded-md text-xs text-gray-500 transition-colors hover:bg-black/5 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/50 disabled:opacity-50 data-[state=open]:bg-black/5"
          disabled={processing || isProcessing}
        >
          {isRetrying ? <Loader className="h-3.5 w-3.5" /> : <MoreHorizontal size={14} />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <ActionMenuItems isRetrying={isRetrying} onRetry={canRetry ? handleRetry : undefined} onEdit={onEdit} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface Props {
  processing: boolean;
  isProcessing: boolean;
  isEdit: boolean;
  businessReportOutcomeId: string;
  businessReportOutcomeResultId: string;
  outcomeId: string;
  hasSnapshotCollection: boolean;
  onCreateEventClick?: () => void;
  events: GetSnapshotEventResponseItem[];
  editedAt: string | null;
  editorName: string | null;
  isBusinessReport?: boolean;
}

export const Actions: React.FC<Props> = ({
  processing,
  isProcessing,
  isEdit,
  businessReportOutcomeId,
  businessReportOutcomeResultId,
  hasSnapshotCollection,
  events,
  onCreateEventClick,
  editedAt,
  editorName,
  isBusinessReport = false,
}) => {
  const navigate = useNavigate();
  const { formatDateTime } = useDateFormatter();

  const handleViewEvent = (eventId: string) => {
    navigate(
      `/snapshot-events?businessReportOutcomeId=${businessReportOutcomeId}&businessReportOutcomeResultId=${businessReportOutcomeResultId}&documentName=&eventId=${eventId}`,
    );
  };

  if (isEdit) {
    return null;
  }

  const canCreateEvent = hasSnapshotCollection && !isBusinessReport;
  if (!editedAt && events.length === 0 && !canCreateEvent) return null;

  return (
    <div className="-mt-2 mb-5 flex items-center gap-1.5 px-1">
      <div className="flex items-center gap-1.5">
        {editedAt && (
          <span className="text-xs font-medium text-gray-500">
            Last edited{editorName ? ` by ${editorName}` : ""} on {formatDateTime(editedAt)}
          </span>
        )}
        {events.length > 0 && (
          <EventsDropdown events={events} disabled={processing || isProcessing} onViewEvent={handleViewEvent} />
        )}
      </div>

      <div className="ml-auto flex items-center gap-1">
        {canCreateEvent && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCreateEventClick}
            disabled={processing || isProcessing}
            className="h-8 gap-1.5"
          >
            <CalendarPlus className="size-3.5" />
            Create Event
          </Button>
        )}
      </div>
    </div>
  );
};
